import axios, { AxiosError, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';
const API_TIMEOUT = 30000; // 30 seconds

// Create axios instance with cookie support
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies for authentication
});

// Auth token management (now using cookies)
export const setAuthToken = () => {
  // Tokens are now in httpOnly cookies managed by the server
  // This function is kept for backward compatibility but does nothing
  // The server sets/clears cookies automatically
};

// Request interceptor - Add CSRF token and logging
apiClient.interceptors.request.use(
  (config) => {
    // Add CSRF token for state-changing requests
    const csrfToken = Cookies.get('csrfToken');
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method?.toUpperCase() || '')) {
      if (csrfToken) {
        config.headers['x-csrf-token'] = csrfToken;
      }
    }

    // Log requests in development
    if (import.meta.env.DEV) {
      console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, config.params || config.data);
    }

    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor with automatic token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log successful responses in development
    if (import.meta.env.DEV) {
      console.log(`✅ ${response.status} ${response.config.url}`, response.data);
    }

    return response;
  },
  async (error: AxiosError) => {
    const { response, request, message, config } = error;
    const originalRequest = config as any;

    // Log errors in development
    if (import.meta.env.DEV) {
      console.error('API Error:', { response, request, message });
    }

    // Handle different error types
    if (response) {
      // Server responded with error status
      const { status, data } = response;

      switch (status) {
        case 401:
          // Unauthorized - try to refresh token before logging out

          // Don't retry refresh endpoint or login endpoint
          if (originalRequest.url?.includes('/refresh-token') ||
            originalRequest.url?.includes('/login') ||
            originalRequest._retry) {
            toast.error('Session expired. Please login again.');
            // Redirect handled by auth store
            return Promise.reject(error);
          }

          // Mark this request as retried
          originalRequest._retry = true;

          if (isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then(() => {
              return apiClient(originalRequest);
            }).catch(err => {
              return Promise.reject(err);
            });
          }

          isRefreshing = true;

          try {
            // Attempt to refresh the token
            await apiClient.post('/auth/refresh-token');

            // Token refreshed successfully
            isRefreshing = false;
            processQueue();

            // Retry the original request
            return apiClient(originalRequest);
          } catch (refreshError) {
            // Refresh failed - log out user
            isRefreshing = false;
            processQueue(refreshError);

            toast.error('Session expired. Please login again.');
            // Redirect handled by auth store
            return Promise.reject(refreshError);
          }

        case 403:
          // Check if it's a CSRF error
          const errorCode = (data as any)?.code;
          if (errorCode === 'CSRF_TOKEN_MISSING' || errorCode === 'CSRF_TOKEN_INVALID') {
            toast.error('Session expired. Please refresh the page.');
          } else {
            toast.error('You do not have permission to perform this action.');
          }
          break;

        case 404:
          // Not found
          toast.error('Requested resource not found.');
          break;

        case 422:
          // Validation error
          const validationMessage = (data as any)?.message || 'Validation error occurred.';
          toast.error(validationMessage);
          break;

        case 429:
          // Rate limit exceeded
          toast.error('Too many requests. Please try again later.');
          break;

        case 500:
          // Server error
          toast.error('Server error occurred. Please try again later.');
          break;

        default:
          // Generic error message
          const errorMessage = (data as any)?.message || 'An unexpected error occurred.';
          toast.error(errorMessage);
      }
    } else if (request) {
      // Network error
      toast.error('Network error. Please check your connection.');
    } else {
      // Request setup error
      toast.error('Request failed. Please try again.');
    }

    return Promise.reject(error);
  }
);

// API endpoints
export const endpoints = {
  // Authentication
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    me: '/auth/me',
    refreshToken: '/auth/refresh-token',
  },

  // Admin
  admin: {
    dashboard: '/admin/dashboard',
    analytics: '/admin/analytics',
    settings: '/admin/settings',
  },

  // Users
  users: {
    list: '/admin/user-management/users',
    details: (id: string) => `/admin/user-management/users/${id}`,
    update: (id: string) => `/admin/user-management/users/${id}`,
    suspend: (id: string) => `/admin/user-management/users/${id}/freeze`,
    activate: (id: string) => `/admin/user-management/users/${id}/freeze`,
    ban: (id: string) => `/admin/user-management/users/${id}/ban`,
    transactions: (id: string) => `/admin/user-management/users/${id}/transactions`,
    gameHistory: (id: string) => `/admin/user-management/users/${id}/game-history`,
    wallets: (id: string) => `/admin/user-management/users/${id}/wallets`,
    balance: (id: string) => `/admin/user-management/users/${id}/balance`,
    message: (id: string) => `/admin/user-management/users/${id}/message`,
    warnings: (id: string) => `/admin/user-management/warnings/user/${id}`,
    issueWarning: (id: string) => `/admin/user-management/warnings/issue/${id}`,
  },

  // Transactions
  transactions: {
    list: '/admin/transactions',
    details: (id: string) => `/admin/transactions/${id}`,
    approve: (id: string) => `/admin/transactions/${id}/approve`,
    reject: (id: string) => `/admin/transactions/${id}/reject`,
    export: '/admin/transactions/export',
  },

  // Games
  games: {
    rounds: '/admin/games/rounds',
    results: '/admin/games/results',
    bets: '/admin/games/bets',
    createResult: '/admin/games/results',
    analytics: '/admin/games/analytics',
    liveRounds: '/admin/games/live-rounds',
    liveStats: '/admin/games/live-stats',
    chickenRoadHistory: '/admin/games/chicken-road/history',
  },

  // Wallets
  wallets: {
    list: '/admin/wallets',
    details: (id: string) => `/admin/wallets/${id}`,
    verify: (id: string) => `/admin/wallets/${id}/verify`,
    flag: (id: string) => `/admin/wallets/${id}/flag`,
    unflag: (id: string) => `/admin/wallets/${id}/unflag`,
  },

  // Reports
  reports: {
    userGrowth: '/admin/reports/user-growth',
    revenue: '/admin/reports/revenue',
    gameAnalytics: '/admin/reports/game-analytics',
    transactionSummary: '/admin/reports/transaction-summary',
  },

  // System
  system: {
    health: '/health',
    logs: '/admin/system/logs',
    backup: '/admin/system/backup',
  },

  // Coin System - Bank Accounts
  coinBankAccounts: {
    list: '/admin/coins/bank-accounts',
    create: '/admin/coins/bank-accounts',
    details: (id: string) => `/admin/coins/bank-accounts/${id}`,
    update: (id: string) => `/admin/coins/bank-accounts/${id}`,
    delete: (id: string) => `/admin/coins/bank-accounts/${id}`,
    setPrimary: (id: string) => `/admin/coins/bank-accounts/${id}/set-primary`,
    toggleActive: (id: string) => `/admin/coins/bank-accounts/${id}/toggle-active`,
  },

  // Coin System - UPI Accounts
  coinUPIAccounts: {
    list: '/admin/coins/upi-accounts',
    active: '/admin/coins/upi-accounts/active',
    create: '/admin/coins/upi-accounts',
    update: (id: string) => `/admin/coins/upi-accounts/${id}`,
    delete: (id: string) => `/admin/coins/upi-accounts/${id}`,
    setPrimary: (id: string) => `/admin/coins/upi-accounts/${id}/set-primary`,
    toggleActive: (id: string) => `/admin/coins/upi-accounts/${id}/toggle-active`,
  },

  // Coin System - Offers
  coinOffers: {
    list: '/admin/coins/offers',
    create: '/admin/coins/offers',
    createBulk: '/admin/coins/offers/bulk',
    details: (id: string) => `/admin/coins/offers/${id}`,
    update: (id: string) => `/admin/coins/offers/${id}`,
    delete: (id: string) => `/admin/coins/offers/${id}`,
    toggleActive: (id: string) => `/admin/coins/offers/${id}/toggle-active`,
    forceUnlock: (id: string) => `/admin/coins/offers/${id}/force-unlock`,
    cleanupExpired: '/admin/coins/offers/cleanup',
    statistics: '/admin/coins/offers/stats',
  },

  // Coin System - Purchases
  coinPurchases: {
    list: '/admin/coins/purchases',
    pending: '/admin/coins/purchases/pending',
    details: (id: string) => `/admin/coins/purchases/${id}`,
    approve: (id: string) => `/admin/coins/purchases/${id}/approve`,
    reject: (id: string) => `/admin/coins/purchases/${id}/reject`,
    statistics: '/admin/coins/purchases/stats',
  },

  // Coin System - Withdrawals
  coinWithdrawals: {
    list: '/admin/coins/withdrawals',
    grouped: '/admin/coins/withdrawals/grouped',
    payments: '/admin/coins/withdrawals/payments',
    byUser: (userId: string) => `/admin/coins/withdrawals/user/${userId}`,
    pending: '/admin/coins/withdrawals/pending',
    approved: '/admin/coins/withdrawals/approved',
    details: (id: string) => `/admin/coins/withdrawals/${id}`,
    approve: (id: string) => `/admin/coins/withdrawals/${id}/approve`,
    reject: (id: string) => `/admin/coins/withdrawals/${id}/reject`,
    pay: (id: string) => `/admin/coins/withdrawals/${id}/pay`,
    manualPay: '/admin/coins/withdrawals/manual-pay',
    manualReject: '/admin/coins/withdrawals/manual-reject', // NEW: Manual rejection with coin deduction
    markPaid: (id: string) => `/admin/coins/withdrawals/${id}/mark-paid`,
    markFailed: (id: string) => `/admin/coins/withdrawals/${id}/mark-failed`,
    statistics: '/admin/coins/withdrawals/stats',
  },

  // Admin Management
  admins: {
    list: '/admin/admins',
    create: '/admin/admins',
    details: (id: string) => `/admin/admins/${id}`,
    update: (id: string) => `/admin/admins/${id}`,
    delete: (id: string) => `/admin/admins/${id}`,
  },
};

// Generic API functions
export const api = {
  get: <T>(url: string, params?: any): Promise<T> =>
    apiClient.get(url, { params }).then(res => res.data),

  post: <T>(url: string, data?: any): Promise<T> =>
    apiClient.post(url, data).then(res => res.data),

  put: <T>(url: string, data?: any): Promise<T> =>
    apiClient.put(url, data).then(res => res.data),

  patch: <T>(url: string, data?: any): Promise<T> =>
    apiClient.patch(url, data).then(res => res.data),

  delete: <T>(url: string): Promise<T> =>
    apiClient.delete(url).then(res => res.data),
};

// Upload file function
export const uploadFile = async (file: File, url: string): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);

  return apiClient.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }).then(res => res.data);
};

// Export CSV/Excel function
export const exportData = async (url: string, filename: string): Promise<void> => {
  try {
    const response = await apiClient.get(url, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], {
      type: response.headers['content-type'] || 'application/octet-stream'
    });

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    toast.success('Export completed successfully');
  } catch (error) {
    console.error('Export error:', error);
    toast.error('Export failed. Please try again.');
  }
};

export default api;